"use client";

import { useState } from "react";
import { Notice, SelectField } from "@/components/tool-kit/primitives";
import {
  alphaOptions,
  ExampleButton,
} from "@/components/tools/stats-tests/kit";
import { FriedmanResults } from "@/components/tools/stats-tests-more/friedman-results";
import {
  MatrixInput,
  matrixProblemText,
} from "@/components/tools/stats-tests-more/matrix-input";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { friedman } from "@/lib/tool-kit/stats-tests-more/friedman";
import {
  conditionNames,
  parseMatrix,
} from "@/lib/tool-kit/stats-tests-more/matrix";

/** English strings. A translated content file overrides these through `ui`. */
export const FRIEDMAN_TEST_CALCULATOR_STRINGS = {
  dataLabel:
    "Scores — one row per subject, one column per condition (paste from a spreadsheet or separate with commas or spaces)",
  dataPlaceholder: "7, 5, 3\n8, 6, 4\n6, 6, 3",
  namesLabel: "Condition names (optional, separated by commas)",
  namesPlaceholder: "Placebo, Ibuprofen, Combination",
  conditionDefault: "Condition {n}",
  subjectsDetected: "one: subject|other: subjects",
  conditionsDetected: "one: condition|other: conditions",
  errorInvalid: "Line {line}: “{token}” is not a number.",
  errorRagged:
    "Line {line} has {got} values, but the first row has {expected}. Every subject needs a score in every condition.",
  alphaLabel: "Significance level (α)",
  tryExample: "Try an example",
  exampleNames: "Placebo, Ibuprofen, Combination",
  exampleData:
    "7, 5, 3\n8, 6, 4\n6, 6, 3\n9, 7, 5\n7, 4, 4\n8, 5, 2\n6, 5, 4\n9, 8, 5\n7, 6, 3\n5, 5, 4\n8, 6, 3\n7, 4, 5",
  results: "Results",
  chiSquare: "χ²F (tie-corrected)",
  df: "Degrees of freedom",
  pValue: "p-value",
  kendallW: "Kendall's W",
  subjects: "Subjects (n)",
  conditions: "Conditions (k)",
  decisionReject:
    "Reject H₀ at α = {alpha}: p = {p} is below α — the conditions do not all rank alike within subjects.",
  decisionKeep:
    "Do not reject H₀ at α = {alpha}: p = {p} is not below α — no evidence that the conditions differ.",
  smallSample:
    "With fewer than 10 subjects the chi-square approximation is rough; exact Friedman tables give a more accurate p-value for small designs.",
  twoConditions:
    "With only two conditions the Friedman test reduces to the sign test; the Wilcoxon signed-rank test is more powerful for paired data like this.",
  summaryHeading: "Rank summary by condition",
  condition: "Condition",
  median: "Median",
  rankSum: "Rank sum",
  meanRank: "Mean rank",
  posthocHeading: "Pairwise comparisons",
  posthocHint:
    "Nemenyi compares rank-sum differences with the studentized range distribution and already controls the family-wise error rate. Conover compares them with a t distribution on {df} degrees of freedom; its Holm column adjusts for the {m} comparisons.",
  comparison: "Comparison",
  rankDifference: "Rank-sum difference",
  q: "q",
  pNemenyi: "p (Nemenyi)",
  pBelow: "< {p}",
  tStat: "t",
  pConover: "p (Conover)",
  pConoverHolm: "p (Conover, Holm)",
  versus: "{a} vs {b}",
  ranksHeading: "Ranks within each subject",
  ranksHint:
    "Each subject's scores are ranked from 1 (lowest) to k (highest); tied scores share their average rank.",
  subject: "Subject",
  ranksHidden: "The rank table is shown for up to 40 subjects.",
  howCalculated: "How this was calculated",
  apaHeading: "APA-style result",
  apaTemplate: "χ²({df}, *N* = {n}) = {chi}, *p* {p}, *W* = {w}",
  apaNote:
    "Report each condition's median (or mean rank), then the pairwise comparisons that locate the difference. W is Kendall's coefficient of concordance.",
  copyApa: "Copy APA result",
  copied: "Copied!",
  prompt:
    "Enter at least two subjects and at least two conditions to see the result.",
  errorTied:
    "Within every subject all the scores are identical, so the ranks carry no information and χ²F cannot be computed.",
} as const;

export default function FriedmanTestCalculatorWidget() {
  const strings = useToolUi(FRIEDMAN_TEST_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [data, setData] = useState("");
  const [names, setNames] = useState("");
  const [alpha, setAlpha] = useState("0.05");

  const parsed = parseMatrix(data);
  const problem = matrixProblemText(parsed, strings, fmt);
  const outcome =
    problem === null && parsed.rows.length >= 2 ? friedman(parsed.rows) : null;
  const labels = conditionNames(names, parsed.header, parsed.width, (n) =>
    t(strings.conditionDefault, { n })
  );

  let notice: string | null = problem;
  if (notice === null && !outcome) {
    notice = strings.prompt;
  } else if (notice === null && outcome && !outcome.ok) {
    notice = outcome.reason === "all-tied" ? strings.errorTied : strings.prompt;
  }

  return (
    <div className="space-y-6">
      <MatrixInput
        data={data}
        fmt={fmt}
        idPrefix="fr"
        locale={locale}
        names={names}
        onData={setData}
        onNames={setNames}
        parsed={parsed}
        text={strings}
      />
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-full max-w-xs">
          <SelectField
            id="fr-alpha"
            label={strings.alphaLabel}
            onChange={setAlpha}
            options={alphaOptions(fmt)}
            value={alpha}
          />
        </div>
        <ExampleButton
          label={strings.tryExample}
          onClick={() => {
            setData(strings.exampleData);
            setNames(strings.exampleNames);
          }}
        />
      </div>
      {outcome?.ok ? (
        <FriedmanResults
          alpha={Number(alpha)}
          fmt={fmt}
          names={labels}
          r={outcome.result}
          strings={strings}
        />
      ) : (
        <Notice>{notice}</Notice>
      )}
    </div>
  );
}
